import * as Schema from "effect/Schema";
import * as SchemaTransformation from "effect/SchemaTransformation";

import { ThreadEnvMode } from "./environment.ts";
import { ProjectScriptIcon } from "./orchestration.ts";

/** File name of the checked-in Cleo project file, resolved at the workspace root. */
export const CLEO_PROJECT_FILE_NAME = "cleo.json";

/**
 * Public URL of the published JSON Schema for {@link CleoProjectFile}.
 * The object lives on the release origin at `schema/cleo.json`. The marketing
 * route renders the same document; its `$id` is this URL.
 */
export const CLEO_PROJECT_FILE_SCHEMA_URL = "https://releases.neucleos.ai/schema/cleo.json";

const CLEO_PROJECT_FILE_PATH_MAX_LENGTH = 512;
const CLEO_PROJECT_FILE_MAX_SCRIPTS = 50;

// Annotations go on the encoded (string) side so they survive into the
// published JSON Schema; decoding still trims and re-validates non-emptiness.
const trimmedNonEmpty = (annotations: { readonly description: string }, maxLength?: number) => {
  const annotated = Schema.String.annotate(annotations);
  const encoded =
    maxLength === undefined
      ? annotated.check(Schema.isNonEmpty())
      : annotated.check(Schema.isNonEmpty(), Schema.isMaxLength(maxLength));
  return encoded.pipe(Schema.decodeTo(encoded, SchemaTransformation.trim()));
};

export const CleoProjectFileScript = Schema.Struct({
  name: trimmedNonEmpty({
    description: "Display name for the script, shown in the Cleo scripts menu.",
  }),
  command: trimmedNonEmpty({
    description: "Shell command executed in a Cleo terminal at the project root.",
  }),
  icon: Schema.optionalKey(
    ProjectScriptIcon.annotate({
      description: 'Icon shown next to the script in the scripts menu. Defaults to "play".',
    }),
  ),
  runOnWorktreeCreate: Schema.optionalKey(
    Schema.Boolean.annotate({
      description:
        "When true, the script runs automatically after a worktree is created for a new thread.",
    }),
  ),
  previewUrl: Schema.optionalKey(
    trimmedNonEmpty({
      description:
        "URL opened in the in-app browser preview when this script runs. Only honored on the desktop build.",
    }),
  ),
  autoOpenPreview: Schema.optionalKey(
    Schema.Boolean.annotate({
      description:
        "When true, automatically open the preview panel at `previewUrl` the moment the script starts.",
    }),
  ),
}).annotate({
  description: "A project script that team members can import into Cleo.",
});
export type CleoProjectFileScript = typeof CleoProjectFileScript.Type;

export const CleoProjectFile = Schema.Struct({
  $schema: Schema.optionalKey(
    Schema.String.annotate({
      description: `URL of the JSON Schema for this file, typically "${CLEO_PROJECT_FILE_SCHEMA_URL}".`,
    }),
  ),
  iconPath: Schema.optionalKey(
    trimmedNonEmpty(
      {
        description:
          'Workspace-relative path to the project icon (e.g. "assets/logo.svg"). Checked before Cleo\'s built-in icon locations.',
      },
      CLEO_PROJECT_FILE_PATH_MAX_LENGTH,
    ),
  ),
  defaultThreadEnvMode: Schema.optionalKey(
    ThreadEnvMode.annotate({
      description:
        'Where new threads start for this repository: "worktree" for a fresh git worktree, "local" for the current checkout. A per-project setting in Cleo overrides this; when neither is set, the global default applies.',
    }),
  ),
  scripts: Schema.optionalKey(
    Schema.Array(CleoProjectFileScript)
      .annotate({
        description: "Project scripts shared with everyone who opens this repository in Cleo.",
      })
      .check(Schema.isMaxLength(CLEO_PROJECT_FILE_MAX_SCRIPTS)),
  ),
}).annotate({
  title: "Cleo project file",
  description: "Checked-in project configuration for Cleo (cleo.json at the repository root).",
});
export type CleoProjectFile = typeof CleoProjectFile.Type;
